function About() {
  const focusAreas = [
    { icon: 'genetics', title: 'Performance-driven breeding' },
    { icon: 'nutrition', title: 'Balanced nutrition programs' },
    { icon: 'egg_alt', title: 'Proper incubation & brooding' },
    { icon: 'health_and_safety', title: 'Hygiene and biosecurity' },
    { icon: 'trending_up', title: 'Long-term flock improvement' },
  ];

  return (
    <section className="relative py-28 px-6 overflow-hidden" id="about">
      {/* Warm background */}
      <div className="absolute inset-0 bg-gradient-to-br from-cream via-white to-cream dark:from-background-dark dark:via-background-dark dark:to-background-dark"></div>

      {/* Subtle decorative pattern */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-primary/5 rounded-full translate-y-1/2 -translate-x-1/3 blur-3xl"></div>

      <div className="relative max-w-[1200px] mx-auto">
        {/* Section Header - Centered */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 text-primary font-bold tracking-widest text-sm uppercase mb-4">
            <div className="w-8 h-[2px] bg-primary"></div>
            Our Legacy
            <div className="w-8 h-[2px] bg-primary"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-earth dark:text-white leading-tight">
            Our Farm Story
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-16 items-stretch">

          {/* Left Side - Images */}
          <div className="flex flex-col gap-5 group">
            {/* Main image group with overlapping secondary */}
            <div className="relative">
              {/* Green accent bar */}
              <div className="absolute -left-3 top-8 bottom-8 w-1.5 bg-gradient-to-b from-primary/80 to-primary/20 rounded-full z-20"></div>

              {/* Main Image */}
              <div className="relative z-10 ml-3">
                <img
                  className="rounded-2xl shadow-2xl w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  src="/images/gallery-3.jpg"
                  alt="Motif Farms - Turkey rearing"
                />
                {/* Image overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
              </div>

              {/* Secondary Image - Overlapping */}
              <div className="absolute -bottom-10 -right-6 z-20 hidden lg:block">
                <div className="relative">
                  <img
                    className="rounded-xl shadow-2xl w-56 h-56 object-cover border-4 border-white dark:border-background-dark transition-transform duration-500 group-hover:scale-105 group-hover:-translate-y-1"
                    src="/images/gallery-1.jpg"
                    alt="Motif Farms - Quality turkeys"
                  />
                  {/* Badge on secondary image */}
                  <div className="absolute -top-3 -left-3 bg-primary text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                    Est. 2021
                  </div>
                </div>
              </div>

              {/* Decorative dots pattern */}
              <div className="absolute -bottom-4 -left-4 grid grid-cols-4 gap-1.5 opacity-20 z-0">
                {Array.from({ length: 16 }).map((_, i) => (
                  <div key={i} className="w-2 h-2 bg-primary rounded-full"></div>
                ))}
              </div>
            </div>

            {/* Third Image - Below, filling remaining space */}
            <div className="relative ml-3 mt-6 flex-1 min-h-0 overflow-hidden rounded-2xl">
              <div className="absolute inset-0">
                <img
                  className="shadow-2xl w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  src="/images/gallery-5a.jpg"
                  alt="Motif Farms - Farm life"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Right Side - Content */}
          <div className="flex flex-col gap-5">
            {/* Intro paragraphs */}
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed font-medium">
              At Motif Farms, we believe quality poultry begins with disciplined breeding, structured management,
              and respect for the natural growth cycle of the bird.
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-base leading-relaxed">
              Located in Medie–Kotoku, Accra, Ghana, Motif Farms was built with a clear mission: to produce
              high-performing turkeys suited for both meat production and sustainable breeding.
            </p>

            {/* Focus Areas - Grid with icons */}
            <div className="mt-2">
              <p className="text-earth dark:text-gray-200 font-bold text-lg mb-4">We focus on:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {focusAreas.map((area, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 bg-white dark:bg-gray-800/50 rounded-xl px-4 py-3 shadow-sm border border-gray-100 dark:border-gray-700/50 hover:shadow-md hover:border-primary/30 transition-all duration-300"
                  >
                    <span className="material-symbols-outlined text-primary text-xl flex-shrink-0">{area.icon}</span>
                    <span className="text-gray-700 dark:text-gray-300 text-sm font-semibold">{area.title}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Selection criteria */}
            <p className="text-gray-600 dark:text-gray-400 text-base leading-relaxed mt-1">
              Our birds are selected not just for size, but for strength, fertility, and adaptability to local conditions.
              From fertile eggs and poults to well-finished meat birds, every product from Motif Farms reflects
              a commitment to structure, consistency, and measurable performance.
            </p>

            {/* Mission Statement - styled blockquote */}
            <div className="relative bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-xl p-5 pl-6 border-l-4 border-primary mt-1">
              <svg className="absolute top-3 right-4 w-8 h-8 text-primary/20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.731-9.57 8.983-10.609l.998 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.986z" />
              </svg>
              <p className="text-earth dark:text-white text-lg font-bold italic leading-relaxed relative z-10">
                We are not just raising turkeys — we are building a reliable turkey production system for Ghana.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div className="group p-5 bg-white dark:bg-gray-800/50 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700/50 hover:shadow-lg hover:border-primary/30 transition-all duration-300 text-center">
                <h3 className="text-3xl font-black text-primary mb-1 group-hover:scale-110 transition-transform duration-300 inline-block">5+</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 font-semibold">Years of Experience</p>
              </div>
              <div className="group p-5 bg-white dark:bg-gray-800/50 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700/50 hover:shadow-lg hover:border-primary/30 transition-all duration-300 text-center">
                <span className="material-symbols-outlined text-3xl text-primary mb-1 group-hover:scale-110 transition-transform duration-300 inline-block">park</span>
                <p className="text-sm text-gray-600 dark:text-gray-400 font-semibold">Free Range Birds</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

export default About;